/**
 * ApplyFlowClarifierModal.jsx — RC 1.49.x Phase 3A (operator-locked
 * 2026-05-06)
 *
 * One-time-per-session clarifier that fires on a user's first
 * "Apply to this job" click and explains what they are about to
 * walk through:
 *   • Source-CV-only path (Baseline-Fit-only pack)
 *   • Tailored-Variant path (unlocks Variant Fit + Interview
 *     Intelligence)
 *   • Cover letter path (additional exportable artefact)
 *   • What's optional, what generates Credits, what happens if
 *     no artefacts are generated
 *   • The user remains in control — NDORFlow never auto-applies
 *
 * Behaviour rules (operator-mandated):
 *   • NO new wizard step, NO backend call, NO billing change,
 *     NO pack-logic change. Pure UI explainer.
 *   • Suppressed for the rest of the browser session once
 *     dismissed (sessionStorage flag), so power-users aren't
 *     re-prompted on every click.
 *   • A "Don't show this again on this device" checkbox writes a
 *     localStorage flag that suppresses the modal across sessions.
 *   • Cancel does NOT open the wizard. Continue dismisses + opens.
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckCircle, FileText, Sparkles, Mail, Coins } from 'lucide-react';

const SESSION_KEY = 'ndor:apply-clarifier:session-dismissed';
const PERSIST_KEY = 'ndor:apply-clarifier:persist-dismissed';

export function shouldShowApplyClarifier() {
  try {
    if (sessionStorage.getItem(SESSION_KEY) === '1') return false;
    if (localStorage.getItem(PERSIST_KEY)   === '1') return false;
  } catch { /* private mode — show conservatively */ }
  return true;
}

export function ApplyFlowClarifierModal({ open, onContinue, onCancel }) {
  const [persist, setPersist] = useState(false);

  // Reset checkbox each time the modal re-opens.
  useEffect(() => { if (open) setPersist(false); }, [open]);

  const handleContinue = () => {
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
      if (persist) localStorage.setItem(PERSIST_KEY, '1');
    } catch { /* no-op */ }
    onContinue?.();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel?.(); }}>
      <DialogContent
        className="max-w-xl max-h-[calc(100dvh-2rem)] overflow-y-auto"
        data-testid="apply-flow-clarifier-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-ndor-gold" />
            How the Apply flow works
          </DialogTitle>
          <DialogDescription className="text-[13px] text-ndor-ink-secondary leading-relaxed">
            You're about to open the application wizard. Here's what
            you'll choose, what's optional, and what each choice
            includes in the resulting pack — so you can decide where
            to spend Credits.
          </DialogDescription>
        </DialogHeader>

        <ul className="space-y-3 text-[13px] leading-relaxed text-ndor-ink-secondary">
          {/* Source CV path — operator-locked 2026-05-07 */}
          <li className="flex gap-3" data-testid="apply-clarifier-path-source-cv">
            <FileText className="h-4 w-4 text-ndor-gold shrink-0 mt-0.5" />
            <div>
              <p className="text-ndor-ink font-medium">Source CV only</p>
              <ul className="list-disc list-outside pl-4 space-y-1 mt-1">
                <li>Source CV is private reference material.</li>
                <li>Baseline Fit is diagnostic only.</li>
                <li>A Source-CV-only flow does not create an exportable pack.</li>
                <li>To create exportable outputs, generate a Tailored CV and/or Cover Letter.</li>
                <li>
                  Interview Intelligence requires <strong>Tailored CV + Variant Fit</strong>.
                </li>
              </ul>
            </div>
          </li>
          {/* Tailored Variant path */}
          <li className="flex gap-3" data-testid="apply-clarifier-path-variant">
            <Sparkles className="h-4 w-4 text-ndor-gold shrink-0 mt-0.5" />
            <div>
              <p className="text-ndor-ink font-medium">Tailored CV (optional)</p>
              <p>
                Generating a <strong>tailored Variant</strong> adapts your
                Source CV to this specific role. Tailoring is charged{' '}
                <em>only when</em> the variant improves on your Source CV's
                NDOR verdict — otherwise it's saved for review at no
                charge. A tailored Variant also unlocks running{' '}
                <strong>Variant Fit</strong> on the Variant in Step 3,
                which surfaces Interview Intelligence on the pack page.
              </p>
            </div>
          </li>
          {/* Cover letter path */}
          <li className="flex gap-3" data-testid="apply-clarifier-path-cover-letter">
            <Mail className="h-4 w-4 text-ndor-gold shrink-0 mt-0.5" />
            <div>
              <p className="text-ndor-ink font-medium">Cover letter (optional)</p>
              <p>
                You can generate a cover letter against your selected CV.
                It becomes an exportable artefact in the pack and stays
                in your Documents library for re-use on similar roles.
                Skip this step entirely if you don't need one.
              </p>
            </div>
          </li>
          {/* Credits */}
          <li className="flex gap-3" data-testid="apply-clarifier-credits">
            <Coins className="h-4 w-4 text-ndor-gold shrink-0 mt-0.5" />
            <div>
              <p className="text-ndor-ink font-medium">Credits</p>
              <p>
                Three actions can spend Credits: tailoring a CV (only if
                it improves), generating a cover letter, and running
                Variant Fit. Selecting an existing CV, browsing variants,
                opening the pack page, and exporting are <strong>free</strong>.
                Source CV Baseline Fit lives on the Jobs page and is
                separate from this wizard.
              </p>
            </div>
          </li>
        </ul>

        <div className="rounded-md border border-ndor-gold/30 bg-ndor-gold/5 px-3 py-2.5 text-[12px] text-ndor-ink-secondary leading-relaxed">
          <p>
            <strong className="text-ndor-ink">Pack composition.</strong>{' '}
            Your pack contains <em>only what you generated and chose</em>.
            A Source-CV-only flow is diagnostic and is not an exportable
            pack — generate a Tailored CV and/or Cover Letter to produce
            exportable outputs. Interview Intelligence requires{' '}
            <strong>Tailored CV + Variant Fit</strong>.
          </p>
          <p className="mt-1.5">
            <strong className="text-ndor-ink">You stay in control.</strong>{' '}
            NDORFlow never auto-applies and never auto-tailors. Every
            submission is yours — pack creation only assembles the
            documents and tracking; you click <em>Apply</em> on the
            employer's site yourself when you're ready.
          </p>
        </div>

        <label
          className="flex items-center gap-2 text-[12px] text-ndor-ink-muted cursor-pointer select-none"
          data-testid="apply-clarifier-persist-label"
        >
          <Checkbox
            checked={persist}
            onCheckedChange={(v) => setPersist(!!v)}
            data-testid="apply-clarifier-persist-checkbox"
          />
          Don't show this again on this device.
        </label>

        <DialogFooter className="gap-2 sm:gap-2 sticky bottom-0 -mx-6 px-6 -mb-6 pb-6 pt-3 bg-background border-t border-ndor-edge/40">
          <Button
            variant="ghost"
            onClick={onCancel}
            data-testid="apply-clarifier-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleContinue}
            className="bg-ndor-gold text-ndor-ink hover:bg-ndor-gold/90 font-semibold"
            data-testid="apply-clarifier-continue"
          >
            Continue to wizard
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
